import { api } from '../lib/api';

export interface Hive {
  id: number;
  apiarySiteId: number | null;
  apiaryName: string | null;
  hiveNo: number | null;
  name: string;
  siteNotes: string | null;
  siteTodoNotes: string | null;
  isActive: number;
  createdAt: string;
  updatedAt: string;
  hiveCode: string | null;
  hiveType: string | null;
  hiveFormatItemId: number | null;
  hiveFormatLabel: string | null;
  broodRoomTypeItemId: number | null;
  broodRoomLabel: string | null;
  hiveStatusItemId: number | null;
  hiveStatusLabel: string | null;
  hiveStatus: string | null;
  queenColor: string | null;
  queenStatusItemId: number | null;
  queenStatusLabel: string | null;
  queenStatus: string | null;
  currentQueenBatchId: number | null;
  currentQueenAssignedAt: string | null;
  currentQueenBatchCode: string | null;
  currentQueenBatchGraftedAt: string | null;
  currentQueenBatchQueenCount: number | null;
  currentQueenMotherHiveSiteId: number | null;
  currentQueenMotherHiveName: string | null;
  currentQueenMotherHiveNo: number | null;
  currentQueenMotherApiaryName: string | null;
  startedAt: string | null;
  endedAt: string | null;
  parentHiveSiteId: number | null;
  hiveNotes: string | null;
  hiveTodoNotes: string | null;
  lastTreatmentAt: string | null;
  lastTreatmentPreparation: string | null;
  lastTreatmentWithdrawalDays: number | null;
  withdrawalUntil: string | null;
  superBoxCount: number;
  logCount: number;
}

export interface HiveChild {
  id: number;
  apiarySiteId: number | null;
  apiaryName: string | null;
  hiveNo: number | null;
  name: string;
  isActive: number;
  hiveCode: string | null;
  hiveType: string | null;
  startedAt: string | null;
  queenColor: string | null;
}

export interface HiveApiaryInfo {
  id: number | null;
  name: string | null;
  address1: string | null;
  address2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  markagare: string | null;
  kontaktinfo: string | null;
  lat: string | null;
  lng: string | null;
  notes: string | null;
  todoNotes: string | null;
}

export interface HiveDetailResponse {
  hive: Hive;
  apiary: HiveApiaryInfo;
  children: HiveChild[];
}

export interface HivePayload {
  name: string;
  apiarySiteId: number;
  hiveNo?: number | null;
  hiveCode?: string;
  hiveType?: string;
  hiveFormatItemId?: number | null;
  broodRoomTypeItemId?: number | null;
  hiveStatusItemId?: number | null;
  hiveStatus?: string;
  queenColor?: string;
  queenStatusItemId?: number | null;
  queenStatus?: string;
  startedAt?: string;
  endedAt?: string;
  parentHiveSiteId?: number | null;
  notes?: string;
  siteNotes?: string;
  todoNotes?: string;
  siteTodoNotes?: string;
  isActive?: boolean;
}

export interface HiveLog {
  id: number;
  hiveSiteId: number;
  logDate: string;
  eventType: string;
  isTreatment: number;
  preparation: string | null;
  preparationProductId?: number | null;
  withdrawalDays: number | null;
  notes: string | null;
  triggerCreatedHiveSiteId: number | null;
  triggerCreatedHiveName: string | null;
}

export interface HiveLogPayload {
  logDate?: string;
  eventType: string;
  isTreatment?: boolean;
  preparation?: string;
  preparationProductId?: number;
  withdrawalDays?: number;
  notes?: string;
  queenColor?: string;
  createSplitHive?: boolean;
  newHiveName?: string;
  newHiveNo?: number;
  newHiveCode?: string;
  newHiveType?: string;
  newHiveFormatItemId?: number;
  newBroodRoomTypeItemId?: number;
  newHiveStatusItemId?: number;
  newHiveStatus?: string;
  newHiveQueenColor?: string;
  newHiveQueenStatusItemId?: number;
  newHiveQueenStatus?: string;
  newHiveNotes?: string;
  newHiveSiteNotes?: string;
}

export interface HiveClosePayload {
  logDate?: string;
  notes: string;
}

export interface MedicineLogRow {
  id: number;
  logDate: string;
  eventType: string;
  preparation: string | null;
  withdrawalDays: number | null;
  notes: string | null;
  hiveSiteId: number;
  hiveName: string;
  apiarySiteId: number | null;
  apiaryName: string | null;
  withdrawalUntil: string | null;
}

export interface HiveMetaItem {
  id: number;
  label: string;
}

export interface HiveQuickUpdatePayload {
  field: 'hiveFormatItemId' | 'broodRoomTypeItemId' | 'hiveStatusItemId' | 'queenStatusItemId' | 'queenColor';
  value: number | string | null;
}

export interface HiveMoveApiaryPayload {
  apiarySiteId: number;
}

export interface HiveSuperBoxProduct {
  id: number;
  name: string;
  defaultUomItemId?: number | null;
}

export interface HiveSuperBoxEntry {
  entryKey: string;
  productId: number;
  productName: string;
  movedAt: string;
  txId: number;
}

export interface HivesMeta {
  lists: {
    HIVE_STATUS: HiveMetaItem[];
    QUEEN_STATUS: HiveMetaItem[];
    HIVE_FORMAT: HiveMetaItem[];
    BROOD_ROOM_TYPES: HiveMetaItem[];
    HIVE_LOG_TYPES: HiveMetaItem[];
  };
  preparations: Array<{
    id: number;
    sku: string | null;
    name: string;
    withdrawalDays: number | null;
    treatmentLengthDays: number | null;
    instructionUrl: string | null;
  }>;
}

export const hivesApi = {
  list: (params?: { q?: string; apiaryId?: number; includeInactive?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set('q', params.q);
    if (params?.apiaryId) sp.set('apiaryId', String(params.apiaryId));
    if (params?.includeInactive) sp.set('includeInactive', '1');
    const q = sp.toString();
    return api.get<{ hives: Hive[] }>('/hives' + (q ? `?${q}` : ''));
  },
  get: (id: number) => api.get<HiveDetailResponse>(`/hives/${id}`),
  create: (payload: HivePayload) => api.post<{ id: number }>('/hives', payload),
  update: (id: number, payload: Partial<HivePayload>) => api.patch<{ ok: boolean }>(`/hives/${id}`, payload),
  close: (id: number, payload: HiveClosePayload) => api.post<{ ok: boolean; logId: number }>(`/hives/${id}/close`, payload),
  nextNumber: () => api.get<{ nextHiveNo: number }>('/hives/next-number'),
  meta: () => api.get<HivesMeta>('/hives/meta'),
  remove: (id: number) => api.delete<{ ok: boolean; action?: 'hidden' | 'deleted'; message?: string }>(`/hives/${id}`),
  quickUpdate: (id: number, payload: HiveQuickUpdatePayload) =>
    api.post<{ ok: boolean; changed: boolean; logId?: number; message?: string }>(`/hives/${id}/quick-update`, payload),
  moveApiary: (id: number, payload: HiveMoveApiaryPayload) =>
    api.post<{ ok: boolean; changed: boolean; logId?: number; message?: string }>(`/hives/${id}/move-apiary`, payload),
  superBoxes: {
    list: (hiveId: number) =>
      api.get<{ availableProducts: HiveSuperBoxProduct[]; installedBoxes: HiveSuperBoxEntry[] }>(`/hives/${hiveId}/super-boxes`),
    add: (hiveId: number, productId: number) =>
      api.post<{ ok: boolean; txId: number; logId: number }>(`/hives/${hiveId}/super-boxes/add`, { productId }),
    remove: (hiveId: number, entryKey: string) =>
      api.post<{ ok: boolean; txId: number; logId: number }>(`/hives/${hiveId}/super-boxes/remove`, { entryKey }),
  },
  logs: {
    list: (hiveId: number) => api.get<{ logs: HiveLog[] }>(`/hives/${hiveId}/logs`),
    create: (hiveId: number, payload: HiveLogPayload) => api.post<{ id: number; triggerCreatedHiveSiteId: number | null }>(`/hives/${hiveId}/logs`, payload),
    remove: (hiveId: number, logId: number) => api.delete<{ ok: boolean }>(`/hives/${hiveId}/logs/${logId}`),
  },
  medicineLog: (params?: { from?: string; to?: string; apiaryId?: number }) => {
    const sp = new URLSearchParams();
    if (params?.from) sp.set('from', params.from);
    if (params?.to) sp.set('to', params.to);
    if (params?.apiaryId) sp.set('apiaryId', String(params.apiaryId));
    const q = sp.toString();
    return api.get<{ rows: MedicineLogRow[] }>('/hives/medicine-log' + (q ? `?${q}` : ''));
  },
};
